import T from 'prop-types';
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';

import defaultImage from '@skippay/core/assets/images/banners/img-registration.png';
import imageBrain from '@skippay/core/assets/images/selfCare/login-brain.png';
import imageHeart from '@skippay/core/assets/images/selfCare/login-heart.png';
import BgCircles from '@skippay/core/components/BgCircles';
import Copyright from '@skippay/core/components/Copyright';
import Image from '@skippay/core/components/Image';
import Link from '@skippay/core/components/Link';
import Logo from '@skippay/core/components/Logo';
import Notifications from '@skippay/core/components/Notifications';
import Step from '@skippay/core/components/Step';
import Text from '@skippay/core/components/Text';
import Wrapped from '@skippay/core/components/Wrapped';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { be, bm } from '@skippay/core/utils/classUtils';

import GatsbyImage from 'components/GatsbyImage';
import { ROOT } from 'constants/routes';
import useCookie from 'hooks/useCookie';

import './index.sass';

const MODULE_NAME = 'AuthLayout';

const AuthLayout = memo(function AuthLayout(props) {
	const {
		aboveContent,
		children,
		contentOnLeft,
		image,
		lightLayout,
		login,
		footer,
		footerContent,
		logoIcon,
		title,
		topContent,
		underTitle,
		sideContent,
		sideContentMobile,
		wide,
	} = props;

	useCookie();

	return (
		<div className={bm(MODULE_NAME, { contentOnLeft, lightLayout })}>
			<BgCircles
				position="fixed"
				{...(contentOnLeft
					? {
							width: '75%',
							bottom: '0',
							right: '0',
							move: '15%, 40%',
						}
					: {
							width: '95%',
							left: '2.5%',
							top: '2.5%',
						})}
			/>
			{login && (
				<div className={be(MODULE_NAME, 'imageHeart')}>
					<Image src={imageHeart} />
				</div>
			)}
			<div className={be(MODULE_NAME, 'logo', 'mobile')}>
				<Link
					testId="headerLogoMobile"
					analyticsId="headerLogoMobile"
					to={ROOT}
					isExternal
					withImage
				>
					<Logo sm />
				</Link>
			</div>
			<div className={be(MODULE_NAME, 'notifications')}>
				<Notifications />
			</div>
			<div className={be(MODULE_NAME, 'container')}>
				<div className="u-pr d-inline-block">
					{login && (
						<div className={be(MODULE_NAME, 'imageBrain')}>
							<Image src={imageBrain} />
						</div>
					)}
					<div className={be(MODULE_NAME, 'content')}>
						<div className={be(MODULE_NAME, 'mainContent', { wide })}>
							{aboveContent && (
								<div className={be(MODULE_NAME, 'aboveContent')}>{aboveContent}</div>
							)}

							{(topContent || title || underTitle || children) && (
								<>
									<div className={be(MODULE_NAME, 'logo')}>
										{logoIcon || (
											<Link testId="headerLogo" analyticsId="headerLogo" to={ROOT} withImage>
												<Logo />
											</Link>
										)}
									</div>

									{topContent}

									<div
										className={be(MODULE_NAME, 'titleSection', null, props.titleSectionClass)}
										data-clarity-unmask="true"
									>
										{title && (
											<Text as="h1" size="h1" align="center">
												{title}
											</Text>
										)}
										{underTitle && (
											<>
												{title && <Step v sm />}
												<Text align="center">{underTitle}</Text>
											</>
										)}
									</div>

									{children}

									{footerContent}
								</>
							)}

							<Wrapped into={footer && <div className={be(MODULE_NAME, 'footer')} />}>
								{sideContentMobile && (
									<div className={be(MODULE_NAME, 'sideContentMobile')}>{sideContentMobile}</div>
								)}
								{footer}
							</Wrapped>

							{!sideContent && (
								<div className="mt-24">
									<Copyright />
								</div>
							)}
						</div>
						{sideContent && <div className={be(MODULE_NAME, 'sideContent')}>{sideContent}</div>}
					</div>
				</div>
			</div>

			{contentOnLeft && (
				<div className={be(MODULE_NAME, 'mainTitleSection')}>
					{image ? (
						<GatsbyImage
							childImageSharp={{
								fixed: { ...image },
							}}
							alt={title}
							maxWidth="890px"
							className={be(MODULE_NAME, 'image')}
						/>
					) : (
						<img src={defaultImage} alt="Skip Pay" className={be(MODULE_NAME, 'image')} />
					)}

					<h2 className={be(MODULE_NAME, 'mainTitle')}>
						{image && props.promoTitle ? (
							<ReactMarkdown>{props.promoTitle}</ReactMarkdown>
						) : (
							!image && (
								<FormattedMessage
									id="AuthLayout.title.new"
									defaultMessage="Vybírejte srdcem,{br}<b>plaťte hlavou</b>"
									values={{
										br: <br />,
										b: (text) => <b>{text}</b>,
									}}
								/>
							)
						)}
					</h2>
				</div>
			)}
		</div>
	);
});

AuthLayout.propTypes = {
	aboveContent: T.node,
	children: T.node,
	image: T.node,
	login: T.bool,
	lightLayout: T.bool,
	footer: T.node,
	footerContent: T.node,
	logoIcon: T.node,
	title: T.node,
	titleSectionClass: T.string,
	topContent: T.node,
	underTitle: T.node,
	contentOnLeft: T.bool,
	promoTitle: T.string,
	sideContent: T.node,
	sideContentMobile: T.node,
	wide: T.bool,
};

export default AuthLayout;
